import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { TrailStewardAddEvent } from '../../events/TrailStewardAddEvent'
import { TrailStewardUpdateEvent } from '../../events/TrailStewardUpdateEvent'

import FlowfeedApiQueryService, {
  FlowfeedApiQueryServiceGetEvent,
} from '../FlowfeedApiQueryService/FlowfeedApiQueryService'
import { TrailStewardSearchEvent } from '../../events/TrailStewardSearchEvent'
import { TrailSteward } from '../../models/TrailSteward.model'
import { TrailStewardDTO } from '../../models/TrailStewardDTO.model'
import { TrailStewardRelations } from '../../models/TrailStewardRelations.model'
import { AppRegions } from '../../models/AppRegions.model'

export class TrailStewardService {
  /**
   * @description Search for a single trail steward by id
   *
   * @param id
   * @param relations
   *
   * @returns promise for a TrailStewardService
   */
  public async get({ id, relations }: { id?: number; relations?: TrailStewardRelations[] }): Promise<TrailSteward> {
    const params = {
      id,
      relations:
        Array.isArray(relations) && !relations.length
          ? 'false' // false is treated as an empty array by the API since an empty string would be treated as default relations normally
          : relations?.join(',') || undefined,
    }
    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: `trail-steward/get`,
      params,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const stewardRaw = await flowfeedApiQueryService.get<TrailStewardDTO>(flowfeedApiQueryRequest)
      return TrailStewardService.formatSteward(stewardRaw)
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  public async search(event: TrailStewardSearchEvent): Promise<PaginatedResponse<TrailSteward>> {
    const params = {
      ...event,
      name: event.name || undefined,
    }

    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: 'trail-steward',
      params,
    }

    const stewards: {
      data: TrailSteward[]
      total: number
    } = {
      data: [],
      total: 0,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const rawStewards = await flowfeedApiQueryService.get<PaginatedResponse<TrailStewardDTO>>(flowfeedApiQueryRequest)
      // wrap to avoid errors if the request is cancelled
      if (rawStewards?.data) {
        stewards.data = rawStewards.data.map((steward) => TrailStewardService.formatSteward(steward))
        stewards.total = rawStewards.total
      }
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }

    return stewards
  }

  public async add(steward: TrailStewardAddEvent): Promise<TrailSteward> {
    const flowfeedApiQueryRequest = {
      endpoint: 'trail-steward',
      data: steward,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const stewardRaw = await flowfeedApiQueryService.post<TrailStewardDTO>(flowfeedApiQueryRequest)
      return TrailStewardService.formatSteward(stewardRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async update(id: number, steward: TrailStewardUpdateEvent): Promise<TrailSteward> {
    const flowfeedApiQueryRequest = {
      endpoint: `trail-steward/${id}`,
      data: steward,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const stewardRaw = await flowfeedApiQueryService.patch<TrailStewardDTO>(flowfeedApiQueryRequest)
      return TrailStewardService.formatSteward(stewardRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async delete(id: number): Promise<void> {
    const flowfeedApiQueryRequest = {
      endpoint: `trail-steward/${id}`,
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.delete(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public static formatSteward(steward: TrailStewardDTO): TrailSteward {
    return {
      id: steward.id,
      name: steward.name,
      email: steward.email,
      phone: steward.phone,
      region: {
        code: steward.region.code as AppRegions,
        name: steward.region.name || '',
      },
      trailSystems: steward.trailSystems,
      updatedAt: new Date(steward.updatedAt),
      createdAt: new Date(steward.createdAt),
      deletedAt: new Date(steward.deletedAt),
    }
  }
}
